import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Circle, ArrowRight, Map, NotebookPen, Plus, Compass, Sparkles,
} from "lucide-react";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EntryComposer } from "@/components/journey/EntryComposer";
import { EntryCard } from "@/components/journey/EntryCard";
import { SuggestEntityDialog } from "@/components/journey/SuggestEntityDialog";
import { ResearchPanel } from "@/components/ai/ResearchPanel";
import { ResearchExtractDialog } from "@/components/ai/ResearchExtractDialog";
import { useAuth } from "@/context/AuthContext";

const typeLabel = (t) => (t || "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const PathView = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCurator = user && (user.role === "curator" || user.role === "admin");
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [target, setTarget] = useState(null); // null = path-level, or {node_id, title}
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestPrefill, setSuggestPrefill] = useState(null);
  const [researchOpen, setResearchOpen] = useState(false);
  const [extractNote, setExtractNote] = useState(null); // a note whose text to extract entities from

  const openSuggest = (pre = null) => { setSuggestPrefill(pre); setSuggestOpen(true); };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/learner/path/${trackId}`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
    }
  }, [trackId]);

  useEffect(() => { load(); }, [load]);

  const toggleLearned = async (nodeId) => {
    try { await api.post("/progress/toggle", { node_id: nodeId }); load(); }
    catch { toast.error("Could not update progress"); }
  };

  const addEntry = async (payload) => {
    try {
      await api.post("/notes", {
        ...payload,
        node_id: target ? target.node_id : null,
        track_id: target ? null : trackId,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const deleteEntry = async (id) => {
    try { await api.delete(`/notes/${id}`); load(); }
    catch { toast.error("Could not delete"); }
  };

  const suggestEntry = async (entry) => {
    try {
      await api.post("/contributions", {
        entry_type: entry.entry_type, text: entry.text, url: entry.url,
        label: entry.label, node_id: entry.node_id, track_id: entry.track_id, note_id: entry.id,
      });
      toast.success("Sent to curators for review");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not suggest"); }
  };

  const saveResearchToNotes = async (text, sources) => {
    try {
      await api.post("/notes", { entry_type: "note", track_id: trackId, text });
      for (const s of (sources || []).slice(0, 6)) {
        if (s.url) await api.post("/notes", { entry_type: "link", track_id: trackId, url: s.url, label: s.title });
      }
      toast.success("Saved research + sources to your notes");
      setResearchOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const leave = async () => {    try { await api.delete(`/learner/paths/${trackId}/enroll`); toast.success("Left path"); navigate("/journey"); }
    catch { toast.error("Could not leave path"); }
  };

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center relative z-10">
        <Compass className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
        <h1 className="font-serif text-3xl text-foreground">Path not found</h1>
        <Link to="/journey" className="text-primary hover:underline mt-3 inline-block">Back to My Journey</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-16 text-muted-foreground relative z-10">Loading path{"\u2026"}</div>;

  const { track, next_unlearned: next, notes } = data;
  const pct = track.total ? Math.round((track.learned / track.total) * 100) : 0;
  const chartData = [{ name: "progress", value: pct, fill: "hsl(16 60% 49%)" }];

  const groups = {};
  track.nodes.forEach((n) => { (groups[n.type] = groups[n.type] || []).push(n); });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 relative z-10">
      <Link to="/journey" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-journey">
        <ArrowLeft className="w-4 h-4" /> My Journey
      </Link>

      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="mt-4 bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col md:flex-row md:items-center gap-6" data-testid="path-hero">
        <div className="w-24 h-24 relative shrink-0">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={chartData} startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: "hsl(40 30% 92%)" }} dataKey="value" cornerRadius={20} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-serif text-xl text-foreground">{pct}%</span>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-primary">Learning Path</div>
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight-serif text-foreground mt-1">{track.title}</h1>
          {track.description && <p className="text-muted-foreground mt-1 max-w-2xl">{track.description}</p>}
          <div className="text-sm text-muted-foreground mt-2">{track.learned} of {track.total} entities learned</div>
        </div>
        <div className="flex md:flex-col gap-2 shrink-0">
          <Button asChild variant="outline" size="sm" data-testid="view-on-map-btn">
            <Link to={`/explore?view=map&node=${track.id}`}><Map className="w-4 h-4 mr-1.5" /> View on the map</Link>
          </Button>
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={leave} data-testid="leave-path-btn">
            Leave path
          </Button>
        </div>
      </motion.div>

      {/* Continue nudge */}
      {next ? (
        <div className="mt-4 bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="continue-nudge">
          <div className="flex items-center gap-3">
            <ArrowRight className="w-5 h-5 text-primary shrink-0" />
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Continue with</div>
              <div className="font-serif text-lg text-foreground">{next.title}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={`/explore?focus=${next.id}`}>Open in Explore</Link>
            </Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => toggleLearned(next.id)} data-testid="continue-mark-learned">
              Mark learned
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 bg-accent/10 border border-accent/30 rounded-lg p-4 flex items-center gap-3" data-testid="path-complete">
          <CheckCircle2 className="w-5 h-5 text-accent" />
          <div className="text-sm text-foreground">You{"\u2019"}ve learned every entity in this path. Beautifully done.</div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Curriculum spine */}
        <div className="lg:col-span-7" data-testid="curriculum-spine">
          <h2 className="font-serif text-2xl text-foreground mb-4">Curriculum</h2>
          <div className="space-y-6">
            {Object.entries(groups).map(([type, items]) => (
              <div key={type}>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{typeLabel(type)} · {items.length}</div>
                <div className="bg-card border border-border rounded-lg divide-y divide-border">
                  {items.map((n) => (
                    <div key={n.id} className="px-4 py-3 flex items-center gap-3" data-testid={`spine-node-${n.id}`}>
                      <button onClick={() => toggleLearned(n.id)} data-testid={`toggle-learned-${n.id}`} className="shrink-0">
                        {n.learned
                          ? <CheckCircle2 className="w-5 h-5 text-accent" />
                          : <Circle className="w-5 h-5 text-muted-foreground/40 hover:text-primary transition-colors" />}
                      </button>
                      <span className={`flex-1 text-sm ${n.learned ? "text-foreground" : "text-muted-foreground"}`}>{n.title}</span>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-primary"
                        onClick={() => setTarget({ node_id: n.id, title: n.title })} data-testid={`add-node-note-${n.id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Note
                      </Button>
                      <Link to={`/explore?focus=${n.id}`} className="text-xs text-primary hover:underline shrink-0" data-testid={`open-explore-${n.id}`}>
                        Explore
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes & Resources */}
        <aside className="lg:col-span-5">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <NotebookPen className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl text-foreground">Notes & Resources</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setResearchOpen(true)} data-testid="research-with-ai-btn"
                className="bg-[#ffb020] hover:bg-[#e69a10] text-[#14140f]">
                <Sparkles className="w-4 h-4 mr-1.5" /> Research with AI
              </Button>
              <Button variant="outline" size="sm" onClick={() => openSuggest()} data-testid="suggest-entity-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> Suggest an entity
              </Button>
            </div>
          </div>
          <div className="lg:sticky lg:top-20 space-y-4">
            <EntryComposer
              target={target}
              onClearTarget={() => setTarget(null)}
              onAdd={addEntry}
              onSuggestEntity={(draft) => openSuggest({ text: draft.text, url: draft.url })}
            />
            {notes.length === 0 ? (
              <div className="bg-card border border-border border-dashed rounded-lg p-8 text-center text-muted-foreground text-sm" data-testid="entries-empty">
                No notes yet. Capture a thought, a link, a quote, or a question as you learn.
              </div>
            ) : (
              <div className="space-y-3" data-testid="entries-list">
                {notes.map((e) => (
                  <EntryCard key={e.id} entry={e} onSuggest={suggestEntry} onDelete={deleteEntry} onExtract={setExtractNote} />
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      <SuggestEntityDialog
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        presetText={suggestPrefill?.text || ""}
        presetUrl={suggestPrefill?.url || ""}
        presetTrack={{ id: track.id, label: track.title }}
      />

      <ResearchPanel
        open={researchOpen}
        onOpenChange={setResearchOpen}
        contextLabel={track.title}
        onSaveToNotes={saveResearchToNotes}
        enableExtract={isCurator}
        onExtracted={load}
      />

      {extractNote && (
        <ResearchExtractDialog
          open={!!extractNote}
          onOpenChange={(o) => !o && setExtractNote(null)}
          text={extractNote.text}
          sourceLabel={`From note (${new Date(extractNote.created_at).toLocaleDateString()})`}
          onCommitted={() => setExtractNote(null)}
        />
      )}
    </div>
  );
};

export default PathView;
